/* ----------------------------------------------------------------------------
 * Deterministic mock TTS — zero network, node-testable, local-demo safe.
 *
 * Produces a deterministic byte stream derived from the text (so the same text
 * yields the same "audio" — good for cache-key + round-trip tests) and honours
 * cancel() (barge-in) by ending the stream early. No real audio, no provider.
 * No TS parameter properties (strip-only Node test runner compatibility).
 * -------------------------------------------------------------------------- */

import type {
  TtsAdapter,
  TtsAudioChunk,
  TtsSynthesis,
  TtsSynthesizeOptions,
} from './TtsAdapter.ts';

class MockTtsSynthesis implements TtsSynthesis {
  readonly provider = 'mock';
  private cancelled = false;
  private readonly text: string;
  private readonly opts: TtsSynthesizeOptions;

  constructor(text: string, opts: TtsSynthesizeOptions) {
    this.text = text;
    this.opts = opts;
  }

  cancel(): void {
    this.cancelled = true;
  }

  async *stream(): AsyncGenerator<TtsAudioChunk, void, unknown> {
    // One chunk per ~40-char span; bytes are a deterministic function of the
    // character codes + voice, so identical (text, voice) => identical bytes.
    const voiceSalt = this.opts.voice.length & 0xff;
    const span = 40;
    let seq = 0;
    for (let i = 0; i < this.text.length; i += span) {
      if (this.cancelled) return;
      const slice = this.text.slice(i, i + span);
      const bytes = new Uint8Array(slice.length);
      for (let j = 0; j < slice.length; j++) {
        bytes[j] = (slice.charCodeAt(j) + voiceSalt + seq) & 0xff;
      }
      yield { seq, bytes };
      seq++;
      // Yield to the event loop so cancel() can interleave (still no timers).
      await Promise.resolve();
    }
  }
}

export class MockTtsAdapter implements TtsAdapter {
  readonly provider = 'mock';

  synthesize(text: string, opts: TtsSynthesizeOptions): TtsSynthesis {
    return new MockTtsSynthesis(text, opts);
  }
}

export { MockTtsSynthesis };
